// ── Shared pathfinding / wall-edge utilities ──────────────────────────────
// Extracted from TokenLayer so BuffPanel (mirror image) can reuse them.

// Catmull-Rom spline — approximates Konva's tension-based curve for collision
function splinePoints(pts, closed) {
  if (pts.length < 3) return pts;
  const result = [];
  const n = pts.length;
  const count = closed ? n : n - 1;
  const STEPS = 8;

  for (let i = 0; i < count; i++) {
    const p0 = pts[Math.max(0, (i - 1 + n) % n)];
    const p1 = pts[i % n];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[Math.min(n - 1, (i + 2) % n)];
    if (!closed && i === 0) { /* p0 stays as pts[0] */ }
    if (!closed && i === n - 2) { /* p3 stays as pts[n-1] */ }

    for (let s = 0; s < STEPS; s++) {
      const t = s / STEPS;
      const t2 = t * t, t3 = t2 * t;
      result.push({
        x: 0.5 * ((2*p1.x) + (-p0.x+p2.x)*t + (2*p0.x-5*p1.x+4*p2.x-p3.x)*t2 + (-p0.x+3*p1.x-3*p2.x+p3.x)*t3),
        y: 0.5 * ((2*p1.y) + (-p0.y+p2.y)*t + (2*p0.y-5*p1.y+4*p2.y-p3.y)*t2 + (-p0.y+3*p1.y-3*p2.y+p3.y)*t3),
      });
    }
  }
  if (!closed) result.push(pts[n - 1]);
  return result;
}

// Flatten curved walls into many-point straight-segment walls for collision
export function flattenWalls(walls) {
  return walls.map((wall) => {
    if (!wall.curved || !wall.pts || wall.pts.length < 3) return wall;
    return { ...wall, pts: splinePoints(wall.pts, wall.closed), curved: false };
  });
}

export function edgeKey(ax, ay, bx, by) {
  return ax < bx || (ax === bx && ay < by)
    ? `${ax},${ay}->${bx},${by}`
    : `${bx},${by}->${ax},${ay}`;
}

export function buildWallEdges(walls) {
  const edges = new Set();
  if (!walls || walls.length === 0) return edges;
  const EPS = 1e-9;

  for (const wall of flattenWalls(walls)) {
    const pts = wall.pts;
    if (!pts || pts.length < 2) continue;
    const segs = [];
    for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
    if (wall.closed && pts.length > 2) segs.push([pts[pts.length - 1], pts[0]]);

    for (const [p1, p2] of segs) {
      const x1 = p1.x, y1 = p1.y, x2 = p2.x, y2 = p2.y;
      const dx = x2 - x1, dy = y2 - y1;

      if (Math.abs(dx) < EPS && Math.abs(Math.round(x1) - x1) < EPS) {
        const k    = Math.round(x1);
        const yA   = Math.min(y1, y2), yB = Math.max(y1, y2);
        const rMin = Math.floor(yA + EPS);
        const rMax = Math.ceil(yB - EPS) - 1;
        for (let r = rMin; r <= rMax; r++) {
          edges.add(edgeKey(k - 1, r, k, r));
        }
        continue;
      }

      if (Math.abs(dy) < EPS && Math.abs(Math.round(y1) - y1) < EPS) {
        const k    = Math.round(y1);
        const xA   = Math.min(x1, x2), xB = Math.max(x1, x2);
        const cMin = Math.floor(xA + EPS);
        const cMax = Math.ceil(xB - EPS) - 1;
        for (let c = cMin; c <= cMax; c++) {
          edges.add(edgeKey(c, k - 1, c, k));
        }
        continue;
      }

      if (Math.abs(dx) > EPS) {
        const xA = Math.min(x1, x2), xB = Math.max(x1, x2);
        for (let k = Math.ceil(xA + EPS); k <= Math.floor(xB - EPS); k++) {
          const t  = (k - x1) / dx;
          const yi = y1 + t * dy;
          const cy = Math.floor(yi - EPS);
          edges.add(edgeKey(k - 1, cy, k, cy));
        }
      }

      if (Math.abs(dy) > EPS) {
        const yA = Math.min(y1, y2), yB = Math.max(y1, y2);
        for (let k = Math.ceil(yA + EPS); k <= Math.floor(yB - EPS); k++) {
          const t  = (k - y1) / dy;
          const xi = x1 + t * dx;
          const cx = Math.floor(xi - EPS);
          edges.add(edgeKey(cx, k - 1, cx, k));
        }
      }
    }
  }
  return edges;
}

export function buildWallBlockedCells(walls) {
  const blocked = new Set();
  if (!walls || walls.length === 0) return blocked;
  for (const wall of flattenWalls(walls)) {
    const pts = wall.pts;
    if (!pts || pts.length < 2) continue;
    const segs = [];
    for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
    if (wall.closed && pts.length > 2) segs.push([pts[pts.length - 1], pts[0]]);
    for (const [p1, p2] of segs) {
      const len   = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const steps = Math.ceil(len * 20);
      for (let s = 0; s <= steps; s++) {
        const t  = s / Math.max(steps, 1);
        const x  = p1.x + t * (p2.x - p1.x);
        const y  = p1.y + t * (p2.y - p1.y);
        const cx = Math.floor(x);
        const cy = Math.floor(y);
        const lx = x - cx; const ly = y - cy;
        if (lx > 0.05 && lx < 0.95 && ly > 0.05 && ly < 0.95) {
          blocked.add(`${cx},${cy}`);
        }
      }
    }
  }
  return blocked;
}

export function wallBlocksStep(fx, fy, tx, ty, size, wallEdges) {
  if (!wallEdges || wallEdges.size === 0) return false;
  const dx = tx - fx, dy = ty - fy;
  if (dx === 1) {
    for (let s = 0; s < size; s++)
      if (wallEdges.has(edgeKey(fx + size - 1, fy + s, fx + size, fy + s))) return true;
  } else if (dx === -1) {
    for (let s = 0; s < size; s++)
      if (wallEdges.has(edgeKey(fx - 1, fy + s, fx, fy + s))) return true;
  } else if (dy === 1) {
    for (let s = 0; s < size; s++)
      if (wallEdges.has(edgeKey(fx + s, fy + size - 1, fx + s, fy + size))) return true;
  } else if (dy === -1) {
    for (let s = 0; s < size; s++)
      if (wallEdges.has(edgeKey(fx + s, fy - 1, fx + s, fy))) return true;
  }
  return false;
}

// Cost to enter (nx, ny) with a token of given footprint. Cost is 2 if any
// cell of the footprint is difficult (terrain-tagged or in difficultCells);
// a prone mover treats every cell as difficult; a flying mover ignores all.
function cellEntryCost(nx, ny, tokenSize, cells, gridW, difficultCells, moverProne, flying) {
  if (flying) return 1;
  if (moverProne) return 2;
  for (let dx = 0; dx < tokenSize; dx++) {
    for (let dy = 0; dy < tokenSize; dy++) {
      const cx = nx + dx, cy = ny + dy;
      if (difficultCells && difficultCells.has(`${cx},${cy}`)) return 2;
      const cell = cells[cy * gridW + cx];
      if (cell?.difficult) return 2;
    }
  }
  return 1;
}

/**
 * Weighted BFS from (startX, startY) up to speedCells cost.
 * Returns { reachable: Set<"x,y">, cost: Map<"x,y", number> }.
 * Difficult cells (terrain or creature-space) cost 2 to enter; everything else 1.
 */
export function computeReachable(
  startX, startY, speedCells, tokenSize,
  cells, blockedCells, gridW, gridH,
  wallEdges, wallBlockedCells,
  flying = false,
  difficultCells = null,
  moverProne = false,
) {
  const reachable = new Set();
  const cost      = new Map();
  cost.set(`${startX},${startY}`, 0);

  // Bucket queue keyed by accumulated cost — handles integer weights cleanly.
  const buckets = [];
  const push = (d, x, y) => {
    if (!buckets[d]) buckets[d] = [];
    buckets[d].push([x, y]);
  };
  push(0, startX, startY);

  for (let d = 0; d <= speedCells; d++) {
    const level = buckets[d];
    if (!level) continue;
    buckets[d] = null;
    // FIFO inside each bucket — same-cost ties go to whoever pushed first.
    // Pushing orthogonals before diagonals (see neighbor order) means straight
    // paths win over diagonal ones at equal cost, avoiding visual zigzag.
    let head = 0;
    while (head < level.length) {
      const [x, y] = level[head++];
      if ((cost.get(`${x},${y}`) ?? Infinity) < d) continue;
      if (d > 0) reachable.add(`${x},${y}`);
      if (d >= speedCells) continue;

      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
        if (nx < 0 || nx >= gridW || ny < 0 || ny >= gridH) continue;

        if (!flying) {
          if (wallBlocksStep(x, y, nx, ny, tokenSize, wallEdges)) continue;

          let blocked = false;
          for (let dx = 0; dx < tokenSize && !blocked; dx++) {
            for (let dy = 0; dy < tokenSize && !blocked; dy++) {
              const cidx = (ny + dy) * gridW + (nx + dx);
              const cell = cells[cidx];
              if (!cell || cell.terrain === 'empty' || !cell.passable) blocked = true;
              if (blockedCells?.has(`${nx + dx},${ny + dy}`)) blocked = true;
              if (wallBlockedCells?.has(`${nx + dx},${ny + dy}`)) blocked = true;
            }
          }
          if (blocked) continue;
        }

        const entry   = cellEntryCost(nx, ny, tokenSize, cells, gridW, difficultCells, moverProne, flying);
        const newCost = d + entry;
        if (newCost > speedCells) continue;
        const key = `${nx},${ny}`;
        const prev = cost.get(key);
        if (prev !== undefined && prev <= newCost) continue;
        cost.set(key, newCost);
        push(newCost, nx, ny);
      }
    }
  }
  return { reachable, cost };
}

/**
 * Dijkstra-style pathfinding from (startX,startY) to (endX,endY) with
 * per-cell entry costs. Returns { path: [{x,y},...], cost: number } — path
 * excludes start and includes end. Returns { path: null, cost: Infinity }
 * if unreachable.
 */
export function findPath(
  startX, startY, endX, endY, tokenSize,
  cells, blockedCells, gridW, gridH,
  wallEdges, wallBlockedCells,
  flying = false,
  difficultCells = null,
  moverProne = false,
) {
  if (startX === endX && startY === endY) return { path: [], cost: 0 };

  const startKey = `${startX},${startY}`;
  const endKey   = `${endX},${endY}`;
  const best     = new Map();
  const parent   = new Map();
  best.set(startKey, 0);
  parent.set(startKey, null);

  const buckets = [];
  const push = (d, x, y) => {
    if (!buckets[d]) buckets[d] = [];
    buckets[d].push([x, y]);
  };
  push(0, startX, startY);

  const cellPassable = (nx, ny) => {
    if (nx < 0 || nx >= gridW || ny < 0 || ny >= gridH) return false;
    if (flying) return true;
    for (let dx = 0; dx < tokenSize; dx++)
      for (let dy = 0; dy < tokenSize; dy++) {
        const cidx = (ny + dy) * gridW + (nx + dx);
        const cell = cells[cidx];
        if (!cell || cell.terrain === 'empty' || !cell.passable) return false;
        if (blockedCells?.has(`${nx + dx},${ny + dy}`)) return false;
        if (wallBlockedCells?.has(`${nx + dx},${ny + dy}`)) return false;
      }
    return true;
  };

  // Upper bound for bucket iteration — grid size × max cost-per-step (2).
  const MAX_COST = gridW * gridH * 2;

  for (let d = 0; d <= MAX_COST; d++) {
    const level = buckets[d];
    if (!level) continue;
    buckets[d] = null;
    // FIFO inside each bucket — straight-line paths beat diagonal ties.
    let head = 0;
    while (head < level.length) {
      const [x, y] = level[head++];
      const key = `${x},${y}`;
      if ((best.get(key) ?? Infinity) < d) continue;
      if (key === endKey) {
        const path = [];
        let cur = endKey;
        while (cur && cur !== startKey) {
          const [px, py] = cur.split(',').map(Number);
          path.unshift({ x: px, y: py });
          cur = parent.get(cur);
        }
        return { path, cost: d };
      }

      const neighbors = [
        [x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1],
        [x - 1, y - 1], [x + 1, y - 1], [x - 1, y + 1], [x + 1, y + 1],
      ];
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || nx >= gridW || ny < 0 || ny >= gridH) continue;
        const diag = nx !== x && ny !== y;
        if (!flying) {
          if (diag) {
            if (!cellPassable(nx, y) || !cellPassable(x, ny)) continue;
            if (wallBlocksStep(x, y, nx, y, tokenSize, wallEdges)) continue;
            if (wallBlocksStep(x, y, x, ny, tokenSize, wallEdges)) continue;
          } else if (wallBlocksStep(x, y, nx, ny, tokenSize, wallEdges)) {
            continue;
          }
          if (!cellPassable(nx, ny)) continue;
        }
        const entry  = cellEntryCost(nx, ny, tokenSize, cells, gridW, difficultCells, moverProne, flying);
        const nd     = d + entry;
        const nk     = `${nx},${ny}`;
        const prev   = best.get(nk);
        if (prev !== undefined && prev <= nd) continue;
        best.set(nk, nd);
        parent.set(nk, key);
        push(nd, nx, ny);
      }
    }
  }
  return { path: null, cost: Infinity };
}
